import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { useGoogleMaps } from '../../../../providers/GoogleMapsProvider';
import {
  LocationItem,
  LocationAddress,
  LocationMap,
  LocationsContainer,
  locationSectionStyles,
  LocationText,
  DesktopLocationMap
} from './styled';
import { ResidenceSection } from '../ResidenceSection';
import { CurrentResidenceQuery } from '../../../../graphql/graphql';
import useScreenSize from '../../../../hooks/useScreenSize';
import { useClient } from '../../../../hooks/useClient';
import { createMapOptions } from './mapOptions';
import { useLazyMapRender } from '../../../../hooks/useLazyMapRender';

const Map = dynamic(
  () => import('../../../ui/MapModal/Map').then(mod => mod.Map),
  {
    ssr: false
  }
);

const MapLoader = dynamic(
  () => import('../../../ui/MapModal/MapLoader').then(mod => mod.MapLoader),
  {
    ssr: false
  }
);

type ResidenceLocations = NonNullable<
  CurrentResidenceQuery['residence']
>['locations'];

interface LocationSectionProps {
  city?: string | null;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  locations?: ResidenceLocations;
}

export const LocationSection: React.FC<LocationSectionProps> = ({
  city,
  address,
  lat,
  lng,
  locations
}) => {
  const { t } = useTranslation();
  const { isSmallOrLargeDesktop } = useScreenSize();
  const isClient = useClient();
  const { mapBlockRef, isMapVisible } = useLazyMapRender();

  const {
    setIsInitializedMap,
    isOpenModal,
    setMapData,
    toggleModal,
    setMapModalData,
    isInitializedMap
  } = useGoogleMaps();

  const coordinates = useMemo(() => {
    if (!lat || !lng) return undefined;
    return { lat, lng };
  }, [lat, lng]);

  const setOpenMapData = useCallback(() => {
    setMapData({
      initialLocation: coordinates,
      pinSelected: isOpenModal,
      isMarkerDraggable: false
    });
    setIsInitializedMap(true);
    setMapModalData({
      modalId: 'residence-map'
    });
    toggleModal(true);
  }, [
    setIsInitializedMap,
    setMapModalData,
    coordinates,
    isOpenModal,
    setMapData,
    toggleModal
  ]);

  const showOnDesktop = !isClient || isSmallOrLargeDesktop;

  useEffect(() => {
    if (!showOnDesktop || !coordinates || !isMapVisible) return;

    setMapData({
      initialLocation: coordinates,
      pinSelected: false,
      isMarkerDraggable: false
    });

    setIsInitializedMap(true);
  }, [
    showOnDesktop,
    coordinates,
    isMapVisible,
    setMapData,
    setIsInitializedMap
  ]);

  const options = useMemo(() => {
    if (!isInitializedMap) return {};
    return createMapOptions();
  }, [isInitializedMap]);

  const fullLocation = `${city}${t('pages.residence.city')}, ${address}`;

  return (
    <ResidenceSection
      styles={locationSectionStyles}
      sectionId="location-section"
    >
      <LocationAddress tag="title_4_new" data-cy="item-location__address">
        {showOnDesktop ? t('search.locations_search.title') : fullLocation}
      </LocationAddress>
      <LocationMap
        role="button"
        onClick={setOpenMapData}
        onKeyPress={setOpenMapData}
        tabIndex={0}
        data-cy="item-location__map"
      />
      {!!locations?.length && (
        <LocationsContainer>
          {locations.map((location, index) => (
            <LocationItem key={index}>
              <LocationText tag="body_4">{location.fullName}</LocationText>
            </LocationItem>
          ))}
        </LocationsContainer>
      )}
      {showOnDesktop && (
        <DesktopLocationMap ref={mapBlockRef} data-cy="residence-map">
          {isMapVisible && (
            <MapLoader>
              <Map mapOptions={options} />
            </MapLoader>
          )}
        </DesktopLocationMap>
      )}
    </ResidenceSection>
  );
};
